import React, { useEffect, useState } from "react";
import { Plus, Trash2, Radar, Loader2, Send, Gauge } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TeamAvatar } from "@/components/TeamAvatar";

const PRESETS = [
  { name: "Navy & Emerald", primary: "#0F172A", accent: "#059669" },
  { name: "Royal Blue", primary: "#1E3A8A", accent: "#2563EB" },
  { name: "Charcoal & Amber", primary: "#1F2937", accent: "#D97706" },
  { name: "Forest", primary: "#14312A", accent: "#10B981" },
  { name: "Burgundy", primary: "#3F1D2E", accent: "#BE123C" },
];

export default function Settings() {
  const { settings, save } = useSettings();
  const [form, setForm] = useState(settings);
  const [team, setTeam] = useState([]);
  const [newMember, setNewMember] = useState({ name: "", email: "", role: "" });

  useEffect(() => setForm(settings), [settings]);
  const loadTeam = () => api.team().then(setTeam);
  useEffect(() => { loadTeam(); }, []);

  const saveBrand = async () => { await save(form); toast.success("Branding updated"); };

  const addMember = async () => {
    if (!newMember.name.trim()) return;
    await api.createTeam(newMember);
    toast.success("Team member added");
    setNewMember({ name: "", email: "", role: "" }); loadTeam();
  };

  const removeMember = async (id) => { await api.deleteTeam(id); toast.success("Removed"); loadTeam(); };

  const [sourcing, setSourcing] = useState(false);
  const [sourceResult, setSourceResult] = useState(null);
  const [allSectors, setAllSectors] = useState([]);
  useEffect(() => { api.sectors().then(setAllSectors); }, []);
  const toggleSector = (name) => setForm((f) => {
    const cur = f.lead_sectors || [];
    return { ...f, lead_sectors: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name] };
  });
  const [copied, setCopied] = useState(false);
  const publicUrl = typeof window !== "undefined" ? `${window.location.origin}/enquire` : "/enquire";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { console.error("Clipboard copy failed", e); }
  };
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const embedCode = `<iframe src="${publicUrl}" width="100%" height="900" style="border:0;border-radius:12px" title="Enquiry form"></iframe>`;
  const copyEmbed = async () => {
    try { await navigator.clipboard.writeText(embedCode); setCopiedEmbed(true); setTimeout(() => setCopiedEmbed(false), 1500); } catch (e) { console.error("Clipboard copy failed", e); }
  };
  const saveDigest = async () => { await save({ digest_enabled: !!form.digest_enabled, digest_email: form.digest_email, app_base_url: form.app_base_url, weekly_summary_enabled: !!form.weekly_summary_enabled }); toast.success("Email settings saved"); };
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestResult, setDigestResult] = useState("");
  const sendTestDigest = async () => {
    setSendingDigest(true); setDigestResult("");
    try {
      const res = await api.sendDigestNow();
      const map = { sent: `Sent to ${form.digest_email}`, no_leads: "No leads in the last 24h to send", disabled: "Digest is turned off", no_recipient: "Add a recipient email first", no_email_key: "Email not configured yet" };
      const msg = map[res.status] || res.status;
      setDigestResult(msg);
      if (res.status === "sent") toast.success("Digest sent"); else toast.message(msg);
    } catch (e) { toast.error("Could not send digest"); } finally { setSendingDigest(false); }
  };
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [weeklyResult, setWeeklyResult] = useState("");
  const sendWeekly = async () => {
    setSendingWeekly(true); setWeeklyResult("");
    try {
      const res = await api.sendWeeklyNow();
      const map = { sent: `Sent to ${form.digest_email}`, disabled: "Weekly summary is turned off", no_recipient: "Add a recipient email first", no_email_key: "Email not configured yet" };
      const msg = map[res.status] || res.status;
      setWeeklyResult(msg);
      if (res.status === "sent") toast.success("Weekly summary sent"); else toast.message(msg);
    } catch (e) { toast.error("Could not send summary"); } finally { setSendingWeekly(false); }
  };
  const saveLeads = async () => {    await save({
      lead_sourcing_enabled: !!form.lead_sourcing_enabled,
      lead_mode: form.lead_mode || "incorporated",
      lead_location: form.lead_location,
      lead_sectors: (form.lead_sectors || []),
      lead_max_per_day: Number(form.lead_max_per_day) || 25,
      auto_assign_member_id: form.auto_assign_member_id || "",
    });
    toast.success("Lead sourcing settings saved");
  };
  const runSourceNow = async () => {
    setSourcing(true); setSourceResult(null);
    try {
      const res = await api.sourceLeadsNow();
      setSourceResult(res);
      if (res.status === "ok") toast.success(`Sourced ${res.created} new lead(s) from Companies House`);
      else if (res.status === "missing_api_key") toast.error("Add your Companies House API key to enable sourcing");
      else if (res.status === "disabled") toast.message("Lead sourcing is currently turned off");
      else toast.error("Sourcing failed — check the API key and try again");
    } catch (e) {
      toast.error("Sourcing request failed");
    } finally { setSourcing(false); }
  };

  const saveOutreach = async () => {
    await save({
      outreach_subject: form.outreach_subject, outreach_body: form.outreach_body,
      outreach_daily_cap: Number(form.outreach_daily_cap) || 50,
      followup_subject: form.followup_subject, followup_body: form.followup_body,
      followup_delay_days: Number(form.followup_delay_days) || 3,
      step3_subject: form.step3_subject, step3_body: form.step3_body,
      step3_delay_days: Number(form.step3_delay_days) || 7,
      auto_lost_enabled: form.auto_lost_enabled ?? true,
      auto_lost_days: Number(form.auto_lost_days) || 7,
      send_window_hour: Number(form.send_window_hour) || 10,
      always_schedule: form.always_schedule ?? false,
    });
    toast.success("Outreach settings saved");
  };

  const saveScoring = async () => { await save({ sector_weights: form.sector_weights || {} }); toast.success("Scoring weights saved"); };
  const setWeight = (name, val) => setForm((f) => ({ ...f, sector_weights: { ...(f.sector_weights || {}), [name]: Number(val) || 0 } }));
  const [rescoring, setRescoring] = useState(false);
  const recalcScores = async () => {
    setRescoring(true);
    try { const res = await api.rescoreAll(); toast.success(`Scores updated for ${res.updated} lead(s)`); }
    catch (e) { toast.error("Could not recalculate"); } finally { setRescoring(false); }
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Change your firm branding, colours and team. Placeholders are safe to edit.</p>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-lg font-bold text-slate-900">Branding</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5"><Label>Firm name</Label><Input data-testid="setting-firm-name" value={form.firm_name || ""} onChange={(e) => setForm({ ...form, firm_name: e.target.value })} /></div>
          <div className="grid gap-1.5"><Label>Tagline</Label><Input data-testid="setting-tagline" value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></div>
          <div className="grid gap-1.5">
            <Label>Primary colour</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200" data-testid="setting-primary-color" />
              <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="font-mono-fin" />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Accent colour</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200" data-testid="setting-accent-color" />
              <Input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="font-mono-fin" />
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-xs text-slate-500">Quick presets</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button key={p.name} onClick={() => setForm({ ...form, primary_color: p.primary, accent_color: p.accent })} className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50">
                <span className="flex"><span className="h-3 w-3 rounded-full" style={{ background: p.primary }} /><span className="-ml-1 h-3 w-3 rounded-full" style={{ background: p.accent }} /></span>
                {p.name}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={saveBrand} data-testid="save-branding" style={{ backgroundColor: "hsl(var(--brand))" }} className="mt-5 text-white">Save branding</Button>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-lg font-bold text-slate-900">Team members</h2>
        <div className="mt-4 space-y-2">
          {team.map((m) => (
            <div key={m.id} data-testid={`team-member-${m.id}`} className="flex items-center justify-between rounded-xl border border-slate-100 p-3">
              <div className="flex items-center gap-3">
                <TeamAvatar member={m} size="lg" />
                <div><p className="text-sm font-semibold text-slate-900">{m.name}</p><p className="text-xs text-slate-500">{m.role}{m.email ? ` · ${m.email}` : ""}</p></div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeMember(m.id)} data-testid={`remove-member-${m.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
            </div>
          ))}
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          <Input placeholder="Name" value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} data-testid="new-member-name" />
          <Input placeholder="Role" value={newMember.role} onChange={(e) => setNewMember({ ...newMember, role: e.target.value })} data-testid="new-member-role" />
          <Input placeholder="Email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} data-testid="new-member-email" />
        </div>
        <Button variant="outline" onClick={addMember} className="mt-3" data-testid="add-member"><Plus className="mr-1 h-4 w-4" /> Add member</Button>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="lead-sourcing-section">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl" style={{ backgroundColor: "hsl(var(--brand-accent) / 0.12)", color: "hsl(var(--brand-accent))" }}>
              <Radar className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-slate-900">Companies House lead sourcing</h2>
              <p className="text-sm text-slate-500">Automatically add newly incorporated UK companies to your New Enquiry column every day at 07:00 UK time.</p>
            </div>
          </div>
          <Switch checked={!!form.lead_sourcing_enabled} onCheckedChange={(v) => setForm({ ...form, lead_sourcing_enabled: v })} data-testid="lead-sourcing-toggle" />
        </div>

        <div className="mt-5 grid gap-1.5">
          <Label>Sourcing mode</Label>
          <Select value={form.lead_mode || "incorporated"} onValueChange={(v) => setForm({ ...form, lead_mode: v })}>
            <SelectTrigger data-testid="lead-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="incorporated" data-testid="lead-mode-incorporated">Newly incorporated (previous day)</SelectItem>
              <SelectItem value="accounts_due" data-testid="lead-mode-accounts-due">Accounts filing due soon (first-time filers, ~30 days)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-400">{form.lead_mode === "accounts_due"
            ? "Finds companies formed ~21 months ago whose first accounts are due within 30 days — a prime moment to pitch."
            : "Adds UK companies incorporated the previous day."}</p>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="grid gap-1.5">
            <Label>Target location</Label>
            <Input data-testid="lead-location" value={form.lead_location || ""} onChange={(e) => setForm({ ...form, lead_location: e.target.value })} placeholder="London" />
          </div>
          <div className="grid gap-1.5">
            <Label>Max leads per day</Label>
            <Input type="number" data-testid="lead-max" value={form.lead_max_per_day ?? 25} onChange={(e) => setForm({ ...form, lead_max_per_day: e.target.value })} />
          </div>
          <div className="grid gap-1.5">
            <Label>Auto-assign new leads to</Label>
            <Select value={form.auto_assign_member_id || "none"} onValueChange={(v) => setForm({ ...form, auto_assign_member_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="auto-assign"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 grid gap-2">
          <Label>Target sectors</Label>
          <div className="flex flex-wrap gap-2">
            {allSectors.map((name) => {
              const active = (form.lead_sectors || []).includes(name);
              return (
                <button
                  type="button"
                  key={name}
                  onClick={() => toggleSector(name)}
                  data-testid={`sector-${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                >
                  {name}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400">Pick the kinds of businesses you want sourced — we map these to the right Companies House codes automatically.</p>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button onClick={saveLeads} data-testid="save-lead-settings" style={{ backgroundColor: "hsl(var(--brand))" }} className="text-white">Save settings</Button>
          <Button variant="outline" onClick={runSourceNow} disabled={sourcing} data-testid="run-source-now">
            {sourcing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Radar className="mr-1 h-4 w-4" />}
            Run now
          </Button>
          {sourceResult?.status === "ok" && <span className="text-sm font-medium text-emerald-600" data-testid="source-result">Added {sourceResult.created} lead(s) · {sourceResult.fetched} fetched</span>}
          {sourceResult?.status === "missing_api_key" && <span className="text-sm font-medium text-amber-600" data-testid="source-result">API key needed — see note below</span>}
        </div>

        <div className="mt-4 rounded-xl bg-slate-50 p-4 text-xs leading-relaxed text-slate-600">
          <p className="font-semibold text-slate-700">Setup note</p>
          <p className="mt-1">This feature uses the free Companies House API. Add your API key (from <span className="font-mono-fin">developer.company-information.service.gov.uk</span>) as the <span className="font-mono-fin">COMPANIES_HOUSE_API_KEY</span> secret and leads will start flowing in automatically. Until then, the daily job runs but finds no key and skips safely.</p>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="scoring-section">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl" style={{ backgroundColor: "rgba(244,63,94,0.12)", color: "#f43f5e" }}>
            <Gauge className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">Lead scoring</h2>
            <p className="text-sm text-slate-500">Leads get a Hot / Warm / Cold badge from sector priority, company type and whether we found an email or website. Give your best sectors a higher weight.</p>
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {(form.lead_sectors || []).length === 0 && <p className="text-sm text-slate-400">Pick target sectors above to set their weights.</p>}
          {(form.lead_sectors || []).map((name) => (
            <div key={name} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 p-3">
              <span className="text-sm text-slate-700">{name}</span>
              <Input type="number" className="w-20" data-testid={`weight-${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} value={(form.sector_weights || {})[name] ?? 10} onChange={(e) => setWeight(name, e.target.value)} />
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button onClick={saveScoring} data-testid="save-scoring" style={{ backgroundColor: "hsl(var(--brand))" }} className="text-white">Save weights</Button>
          <Button variant="outline" onClick={recalcScores} disabled={rescoring} data-testid="recalc-scores">
            {rescoring ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Gauge className="mr-1 h-4 w-4" />}Recalculate all scores
          </Button>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="digest-section">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">Morning lead digest</h2>
            <p className="mt-1 text-sm text-slate-500">Email a summary of overnight leads (Companies House + website) each morning at 08:00 UK time.</p>
          </div>
          <Switch checked={!!form.digest_enabled} onCheckedChange={(v) => setForm({ ...form, digest_enabled: v })} data-testid="digest-toggle" />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label>Send to</Label>
            <Input type="email" data-testid="digest-email" value={form.digest_email || ""} onChange={(e) => setForm({ ...form, digest_email: e.target.value })} placeholder="owner@yourfirm.co.uk" />
          </div>
          <div className="grid gap-1.5">
            <Label>App link in emails</Label>
            <Input data-testid="app-base-url" value={form.app_base_url || ""} onChange={(e) => setForm({ ...form, app_base_url: e.target.value })} placeholder="https://yourfirm.co.uk" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button onClick={saveDigest} data-testid="save-digest" style={{ backgroundColor: "hsl(var(--brand))" }} className="text-white">Save</Button>
          <Button variant="outline" onClick={sendTestDigest} disabled={sendingDigest} data-testid="send-test-digest">
            {sendingDigest ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Send test digest now
          </Button>
          {digestResult && <span className="text-sm font-medium text-slate-600" data-testid="digest-result">{digestResult}</span>}
        </div>

        <div className="mt-6 border-t border-slate-100 pt-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-display text-base font-semibold text-slate-900">Weekly summary</h3>
              <p className="mt-1 text-sm text-slate-500">A Monday round-up (09:00 UK) of new leads, wins and losses from the past week — sent to the same address.</p>
            </div>
            <Switch checked={!!form.weekly_summary_enabled} onCheckedChange={(v) => setForm({ ...form, weekly_summary_enabled: v })} data-testid="weekly-toggle" />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={sendWeekly} disabled={sendingWeekly} data-testid="send-weekly">
              {sendingWeekly ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Send weekly summary now
            </Button>
            {weeklyResult && <span className="text-sm font-medium text-slate-600" data-testid="weekly-result">{weeklyResult}</span>}
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="outreach-section">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl" style={{ backgroundColor: "rgba(99,102,241,0.12)", color: "#6366f1" }}>
            <Send className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">Cold outreach template</h2>
            <p className="text-sm text-slate-500">The email sent when you hit "Send outreach" on a lead. Use the placeholders below — they're filled in per company.</p>
          </div>
        </div>
        <div className="mt-5 grid gap-4">
          <div className="grid gap-1.5">
            <Label>Subject line</Label>
            <Input data-testid="outreach-subject" value={form.outreach_subject || ""} onChange={(e) => setForm({ ...form, outreach_subject: e.target.value })} placeholder="Accounting support for {business_name}" />
          </div>
          <div className="grid gap-1.5">
            <Label>Email body</Label>
            <Textarea data-testid="outreach-body" rows={12} value={form.outreach_body || ""} onChange={(e) => setForm({ ...form, outreach_body: e.target.value })} className="font-mono-fin text-xs leading-relaxed" />
          </div>
          <div className="grid gap-1.5 sm:max-w-[220px]">
            <Label>Daily send cap</Label>
            <Input type="number" data-testid="outreach-cap" value={form.outreach_daily_cap ?? 50} onChange={(e) => setForm({ ...form, outreach_daily_cap: Number(e.target.value) || 0 })} />
            <p className="text-[11px] text-slate-400">Maximum outreach emails per day for the "Email all leads" button.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            <span className="font-semibold text-slate-600">Placeholders:</span>
            {["{contact_name}", "{business_name}", "{firm_name}"].map((p) => (
              <code key={p} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono-fin text-slate-700">{p}</code>
            ))}
          </div>
          <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4" data-testid="followup-block">
            <h3 className="text-sm font-semibold text-slate-800">Follow-up email (second touch)</h3>
            <p className="mt-0.5 text-xs text-slate-500">Sent to leads who haven't replied, via "Send follow-ups" on the Enquiries page or the automatic daily job.</p>
            <div className="mt-3 grid gap-3">
              <div className="grid gap-1.5">
                <Label>Send after (days since first email)</Label>
                <Input type="number" className="sm:max-w-[160px]" data-testid="followup-delay" value={form.followup_delay_days ?? 3} onChange={(e) => setForm({ ...form, followup_delay_days: Number(e.target.value) || 0 })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Follow-up subject</Label>
                <Input data-testid="followup-subject" value={form.followup_subject || ""} onChange={(e) => setForm({ ...form, followup_subject: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Follow-up body</Label>
                <Textarea data-testid="followup-body" rows={8} value={form.followup_body || ""} onChange={(e) => setForm({ ...form, followup_body: e.target.value })} className="font-mono-fin text-xs leading-relaxed" />
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4" data-testid="step3-block">
            <h3 className="text-sm font-semibold text-slate-800">Final nudge (step 3) &amp; auto-Lost</h3>
            <p className="mt-0.5 text-xs text-slate-500">A last email to inbound leads, then silent ones are moved to Lost automatically.</p>
            <div className="mt-3 grid gap-3">
              <div className="grid gap-1.5 sm:max-w-[220px]">
                <Label>Send step 3 after follow-up (days)</Label>
                <Input type="number" data-testid="step3-delay" value={form.step3_delay_days ?? 7} onChange={(e) => setForm({ ...form, step3_delay_days: Number(e.target.value) || 0 })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Step 3 subject</Label>
                <Input data-testid="step3-subject" value={form.step3_subject || ""} onChange={(e) => setForm({ ...form, step3_subject: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Step 3 body</Label>
                <Textarea data-testid="step3-body" rows={7} value={form.step3_body || ""} onChange={(e) => setForm({ ...form, step3_body: e.target.value })} className="font-mono-fin text-xs leading-relaxed" />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-white p-3">
                <div><p className="text-sm font-medium text-slate-700">Auto-move silent leads to Lost</p><p className="text-[11px] text-slate-400">After step 3 with no reply</p></div>
                <Switch data-testid="auto-lost-switch" checked={form.auto_lost_enabled ?? true} onCheckedChange={(v) => setForm({ ...form, auto_lost_enabled: v })} />
              </div>
              <div className="grid gap-1.5 sm:max-w-[220px]">
                <Label>Mark Lost after (days)</Label>
                <Input type="number" data-testid="auto-lost-days" value={form.auto_lost_days ?? 7} onChange={(e) => setForm({ ...form, auto_lost_days: Number(e.target.value) || 0 })} />
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4" data-testid="schedule-block">
            <h3 className="text-sm font-semibold text-slate-800">Send window</h3>
            <p className="mt-0.5 text-xs text-slate-500">Scheduled and sequence emails to inbound leads go out at this hour, weekdays only.</p>
            <div className="mt-3 grid gap-3">
              <div className="grid gap-1.5 sm:max-w-[220px]">
                <Label>Send hour (24h, UK)</Label>
                <Input type="number" min="0" max="23" data-testid="send-window-hour" value={form.send_window_hour ?? 10} onChange={(e) => setForm({ ...form, send_window_hour: Number(e.target.value) || 0 })} />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-white p-3">
                <div><p className="text-sm font-medium text-slate-700">Always schedule bulk sends</p><p className="text-[11px] text-slate-400">Queue "Email inbound" for the next window instead of sending instantly</p></div>
                <Switch data-testid="always-schedule-switch" checked={form.always_schedule ?? false} onCheckedChange={(v) => setForm({ ...form, always_schedule: v })} />
              </div>
            </div>
          </div>
          <div>
            <Button onClick={saveOutreach} data-testid="save-outreach" style={{ backgroundColor: "hsl(var(--brand))" }} className="text-white">Save settings</Button>
          </div>
          <div className="rounded-xl bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-800" data-testid="outreach-note">
            <strong>Compliance note:</strong> Under UK GDPR/PECR, send B2B outreach only where you have a legitimate interest, always include an easy opt-out, and keep volumes reasonable. Companies House leads have no public email until enriched.
          </div>
          <div className="rounded-xl bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-600" data-testid="hunter-note">
            <strong>Email Finder (optional):</strong> Add a <span className="font-mono-fin">HUNTER_API_KEY</span> secret to automatically find a verified contact email during enrichment whenever a company website is found. Until then, enrichment uses the free web scan only.
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="website-form-section">
        <h2 className="font-display text-lg font-bold text-slate-900">Website enquiry form</h2>
        <p className="mt-1 text-sm text-slate-500">Share this link or embed it on your site. Submissions land in your New Enquiry column automatically, tagged "Website".</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <code className="min-w-[220px] flex-1 rounded-lg bg-slate-50 px-3 py-2 font-mono-fin text-sm text-slate-700" data-testid="public-form-link">{publicUrl}</code>
          <Button variant="outline" onClick={copyLink} data-testid="copy-form-link">{copied ? "Copied!" : "Copy link"}</Button>
          <a href="/enquire" target="_blank" rel="noreferrer"><Button variant="outline" data-testid="open-form">Open form</Button></a>
        </div>
        <div className="mt-4">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Embed on your website</Label>
          <pre className="mt-1.5 overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs leading-relaxed text-slate-100" data-testid="embed-code"><code>{embedCode}</code></pre>
          <Button variant="outline" size="sm" className="mt-2" onClick={copyEmbed} data-testid="copy-embed">{copiedEmbed ? "Copied!" : "Copy embed code"}</Button>
        </div>
      </section>
    </div>
  );
}
